import React, { useState } from "react";
import { Loader2, Plus, Trash2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/utils/constants";

export const TeamDialog = ({ open, onClose, teamMembers, onAddMember, onDeleteMember, authPassword, onRefresh }) => {
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("TM");
  const [adding, setAdding] = useState(false);
  const [editingEmail, setEditingEmail] = useState(null);
  const [emailDraft, setEmailDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      await onAddMember({ name: newName.trim(), email: newEmail.trim(), role: newRole });
      setNewName("");
      setNewEmail("");
      setNewRole("TM");
    } finally {
      setAdding(false);
    }
  };

  const handleSaveEmail = async (memberId) => {
    setSaving(true);
    try {
      await axios.put(`${API}/team-members/${memberId}`, { email: emailDraft.trim() }, {
        headers: { "X-Auth-Password": authPassword },
      });
      toast.success("Email updated");
      setEditingEmail(null);
      onRefresh?.();
    } catch {
      toast.error("Failed to update email");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-[#605e5e]">
            Manage Team Members
          </DialogTitle>
          <DialogDescription className="text-sm text-[#8d8b8b]">
            Add or remove team members and set their email for schedule notifications
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex gap-2">
              <Input
                placeholder="Name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="flex-1"
                data-testid="new-member-name-input"
              />
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger className="w-24" data-testid="new-member-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TM">TM</SelectItem>
                </SelectContent>
              </Select>
              <Button
                onClick={handleAdd}
                disabled={adding || !newName.trim()}
                className="bg-[#605e5e] hover:bg-[#4b4a4a]"
                data-testid="add-member-btn"
              >
                {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              </Button>
            </div>
            <Input
              placeholder="Email (optional)"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="text-sm"
              data-testid="new-member-email-input"
            />
          </div>
          
          <Separator />
          
          <ScrollArea className="h-[300px]">
            <div className="space-y-2">
              {teamMembers.length === 0 ? (
                <p className="text-center text-[#8d8b8b] py-8">No team members yet</p>
              ) : (
                teamMembers.map((member) => (
                  <div
                    key={member.id}
                    className="p-3 bg-gray-50 rounded-sm"
                    data-testid={`team-member-${member.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0"
                          style={{ backgroundColor: member.color || '#605e5e' }}
                        >
                          {member.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-[#1f2937]">{member.name}</p>
                          {editingEmail === member.id ? (
                            <div className="flex gap-1 mt-1">
                              <Input
                                value={emailDraft}
                                onChange={e => setEmailDraft(e.target.value)}
                                placeholder="email@example.com"
                                className="h-6 text-xs px-2"
                                autoFocus
                                onKeyDown={e => { if (e.key === 'Enter') handleSaveEmail(member.id); if (e.key === 'Escape') setEditingEmail(null); }}
                                data-testid={`edit-email-input-${member.id}`}
                              />
                              <Button size="sm" className="h-6 px-2 text-xs bg-[#605e5e]" onClick={() => handleSaveEmail(member.id)} disabled={saving}>
                                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}
                              </Button>
                            </div>
                          ) : (
                            <button
                              onClick={() => { setEditingEmail(member.id); setEmailDraft(member.email || ""); }}
                              className="flex items-center gap-1 text-xs text-[#8d8b8b] hover:text-[#605e5e] transition-colors"
                              data-testid={`member-email-${member.id}`}
                            >
                              <Mail className="h-3 w-3" />
                              {member.email || "Add email"}
                            </button>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDeleteMember(member.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                        data-testid={`delete-member-${member.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="close-team-dialog-btn">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
